import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { ForbiddenPage } from "../../pages/errors/ForbiddenPage";
import { paths } from "../paths";
import { useSession } from "../session/SessionContext";
import { decideAccess } from "./access-policy";
import type { AccessPolicy } from "./access-policy";

/** Where the sign-in page should return the user afterwards, and why they were sent there. */
export interface SignInRedirectState {
  readonly from: string;
  readonly reason:
    | "sign_in_required"
    | "mfa_required"
    // AUTH-008: signed out by the idle-session monitor, not by the RouteGuard below.
    | "idle_timeout"
    | "session_revoked";
}

interface RouteGuardProps {
  policy: AccessPolicy;
  children: ReactNode;
}

/** Wraps a protected route (WEB-004). Cosmetic only — the API enforces the same policy. */
export function RouteGuard({ policy, children }: RouteGuardProps) {
  const session = useSession();
  const location = useLocation();
  const decision = decideAccess(session, policy);

  switch (decision.outcome) {
    case "pending":
      return (
        <p role="status" aria-live="polite">
          Checking your session…
        </p>
      );
    case "sign_in_required":
    case "mfa_required": {
      const state: SignInRedirectState = {
        from: `${location.pathname}${location.search}`,
        reason: decision.outcome,
      };
      return <Navigate to={paths.signIn} replace state={state} />;
    }
    case "forbidden":
      return <ForbiddenPage />;
    case "allowed":
      return <>{children}</>;
  }
}
